import { GlobalWebSocketService } from '@/plugins/websocket'
import {
  alertStore,
  dataStore,
  felicaStore,
  modalErrorStore
} from '@/utils/store-accessor'
import { FelicaParameter } from '@/types'
import {
  AlertLevel,
  AlertMessage,
  defaultAlert,
  ErrorMessage,
  userApi
} from '@/api'
import {
  GlobalModalAlertService,
  GlobalModalErrorService
} from '@/plugins/modal'
import { EventBus } from '@/plugins/event_bus'
import { PAGE_LOADING_END, PAGE_LOADING_START } from '@/config/event'

export class FelicaService {
  public read(obj?: { readCompleted?: Function }): void {
    const initialize = felicaStore.endRead

    if (!dataStore.enabledFelica) {
      initialize()
      return
    }

    GlobalWebSocketService.connect(() => {
      if (!GlobalWebSocketService.isOpen() || felicaStore.reading) {
        initialize()
        return
      }

      GlobalWebSocketService.sendFelicaRead(
        (isConnect: boolean, parameter?: FelicaParameter) => {
          if (!isConnect || !parameter) {
            initialize()
            return
          }

          const complete = felicaStore.startRead

          // resultがfalseの時点でアラートメッセージが返ってきていると仮定する
          if (!parameter.result) {
            initialize()
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open()
            return
          }

          complete()
        },
        (isConnect: boolean, parameter?: FelicaParameter) => {
          if (!isConnect || !parameter) {
            initialize()
            return
          }

          const complete = () => {
            initialize()

            if (obj?.readCompleted) {
              obj.readCompleted(parameter?.id ?? '')
            }
          }

          // resultがfalseの時点でアラートメッセージが返ってきていると仮定する
          // 読込完了処理でエラーアラートが発生する事は現時点では想定していないが、念の為設定しておく
          if (!parameter.result) {
            initialize()
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open()
            return
          }

          complete()
        }
      )
    })
  }

  // 今の所、キャンセル直後に行いたい処理は無い
  public readCancel(): void {
    if (!dataStore.enabledFelica) {
      return
    }

    const initialize = felicaStore.endRead

    GlobalWebSocketService.connect(() => {
      if (!GlobalWebSocketService.isOpen() || !felicaStore.reading) {
        initialize()
        return
      }

      // 読込中でオーバーレイを配置する都合、読込キャンセルの成功、失敗、いずれにせよ処理を解除する必要がある
      // また、キャンセルに失敗するという状況が想定できない為、現時点では常に読込（オーバーレイ）を解除とする
      GlobalWebSocketService.sendFelicaReadCancel(
        (isConnect: boolean, parameter?: FelicaParameter) => {
          // resultがfalseの時点でアラートメッセージが返ってきていると仮定する
          // 読込キャンセル処理でエラーアラートが発生する事は現時点では想定していないが、念の為設定しておく
          if (parameter && !parameter.result) {
            alertStore.setItem(
              Object.assign(defaultAlert(), {
                code: parameter.code,
                level: AlertLevel.Error,
                message: parameter.message
              })
            )
            GlobalModalAlertService.open()
          }

          initialize()
        }
      )
    })
  }

  public update(obj: {
    parameter: { userId: string }
    updated?: Function
  }): void {
    const initialize = felicaStore.endRead

    if (!dataStore.enabledFelica) {
      initialize()
      return
    }

    this.read({
      readCompleted: async (felicaId: string) => {
        initialize()

        EventBus.emit(PAGE_LOADING_START)

        try {
          await userApi.updateUserFelicaId({
            id: obj.parameter.userId,
            felicaId
          })

          if (obj?.updated) {
            obj.updated(felicaId)
          }
        } catch (error) {
          if (error instanceof AlertMessage) {
            alertStore.setItems(error.alerts)
            GlobalModalAlertService.open()
          } else if (error instanceof ErrorMessage) {
            modalErrorStore.init(error)
            GlobalModalErrorService.open()
          } else {
            console.error(error)
          }
        } finally {
          EventBus.emit(PAGE_LOADING_END)
        }
      }
    })
  }
}
